import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "antd";
import {
  getEmployeesResignations,
  getFnfSettlementApprover,
  getSalaryHoldTypes,
} from "../../../services/ResignationServices";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import { FaHandshake, FaTasks } from "react-icons/fa";
import { companyID } from "../../../auth";
import FullAndFinalSettlement from "../../../views/pages/Employees/FullAndFinalSettlement";
import { parseISO } from "date-fns";
import Breadcrumbs from "../../../components/Breadcrumbs";
import RowDetailModal from "../../../components/RowDetailModal";
import Loader from "../../../components/Loader";
import ProtectedButton from "../../../Routes/Approuter/ProtectedButton";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";

const EmployeeFnf = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [employeeResignations, setEmployeeResignations] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [fullAndFinalSettlement, setFullAndFinalSettlement] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [action, setAction] = useState(null);
  const [salaryHoldTypes, setSalaryHoldTypes] = useState([]);

  const [fnfApprover, setFnfApprover] = useState(false);
  const employeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const fetchEmployeesResignations = async () => {
    if (company_id && employeeId) {
      try {
        const response = await getEmployeesResignations(company_id);
        const fnfEmployees =
          response.result.length > 0
            ? response.result.filter(
                (resignation) =>
                  resignation.resignation_status == 2 &&
                  resignation.employee_id != employeeId
              )
            : [];
        setEmployeeResignations(fnfEmployees);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchSalaryHoldTypes = async () => {
    try {
      const response = await getSalaryHoldTypes();
      setSalaryHoldTypes(response.result);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFnfSettlementApprover = async (companyId, employeeId) => {
    try {
      const response = await getFnfSettlementApprover(companyId, employeeId);
      if (response && response.fnfApprover == true) {
        setFnfApprover(true);
      } else {
        setFnfApprover(false);
      }
    } catch (error) {
      console.error("Error fetching FNF settlement approver:", error);
      setFnfApprover(false);
    }
  };

  useEffect(() => {
    fetchEmployeesResignations();
    setAction(null);
    fetchSalaryHoldTypes();
    if (company_id || employeeId) {
      fetchFnfSettlementApprover(company_id, employeeId);
    }
  }, [company_id, employeeId]);

  const onRowClick = (record) => {
    setSelectedRow(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedRow(null);
    setExpanded(false);
  };

  const handleSettlement = (record, action) => {
    if (isModalVisible) {
      setFullAndFinalSettlement(true);
      setIsModalVisible(false);
      setAction(action);
      setSelectedEmployee(record);
    } else {
      onRowClick(record);
    }
  };

  const [expanded, setExpanded] = useState(false);

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      render: (text, record) => (
        <span
          className="table-avatar"
          style={{ cursor: "pointer" }}
          onClick={() => onRowClick(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Separation date",
      dataIndex: "resignation_date",
      key: "resignation_date",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>
          {formatDateInTimezone(text)}
        </span>
      ),
    },
    {
      title: "Last working date",
      dataIndex: "last_working_date",
      key: "last_working_date",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>
          {formatDateInTimezone(text)}
        </span>
      ),
    },
    {
      title: "Salary release date",
      dataIndex: "salary_release_date",
      key: "salary_release_date",
      render: (text, record) => (
        <span onClick={() => onRowClick(record)}>
          {formatDateInTimezone(text)}
        </span>
      ),
    },
    {
      title: "Separation status",
      dataIndex: "resignation_status",
      key: "resignation_status",
      render: (text, record) => (
        <span
          className={`badge bg-${
            text == 1 ? "warning" : text == 2 ? "success" : "danger"
          }`}
          onClick={() => onRowClick(record)}
        >
          {text == 1 ? "Pending" : text == 2 ? "Approved" : "Rejected"}
        </span>
      ),
    },
    {
      title: "FNF approval status",
      dataIndex: "fnf_approval_status",
      key: "fnf_approval_status",
      render: (text, record) => (
        <span
          className={`badge bg-${
            text == 1 || text == null
              ? "warning"
              : text == 2
              ? "success"
              : "danger"
          }`}
          onClick={() => onRowClick(record)}
        >
          {text == 1 || text == null
            ? "Pending"
            : text == 2
            ? "Approved"
            : "Rejected"}
        </span>
      ),
    },
    ...(isModalVisible
      ? [
          {
            title: "Salary hold",
            dataIndex: "salary_hold_type_id",
            key: "salary_hold_type_id",
            render: (text) =>
              salaryHoldTypes.find((type) => type.id == text)?.name || "-",
          },
        ]
      : []),

    {
      title: "Action",
      render: (text, record) => {
        const eligibleForSettlement =
          record?.resignation_status === 2 &&
          parseISO(record?.last_working_date)?.setHours(0, 0, 0, 0) <
            new Date()?.setHours(0, 0, 0, 0);
        const verificationPending = record?.fnf_approval_status;
        return (
          <div className="d-flex gap-3 mt-2">
            <ProtectedButton page="employee-fnf" action="create">
              <button
                disabled={eligibleForSettlement ? false : true}
                className={`${
                  isModalVisible ? `btn btn-outline-success` : "dropdown-item"
                }`}
                onClick={() => handleSettlement(record, "fnfSettlement")}
              >
                {isModalVisible ? (
                  (record?.fnf_approval_status === 2 ? "View " : "Final ") +
                  "Settlement"
                ) : (
                  <FaHandshake
                    className={`${
                      eligibleForSettlement ? "text-primary" : "text-muted"
                    }`}
                    size={18}
                    title={
                      (record?.fnf_approval_status === 2 ? "View " : "Final ") +
                      "Settlement"
                    }
                  />
                )}
              </button>
            </ProtectedButton>

            {fnfApprover && (
              <button
                disabled={
                  eligibleForSettlement && verificationPending ? false : true
                }
                className={`${
                  isModalVisible ? `btn btn-outline-success` : "dropdown-item"
                }`}
                onClick={() => handleSettlement(record, "verifyFNFSettlement")}
              >
                {isModalVisible ? (
                  (record?.fnf_approval_status === 2 ? "View " : "Verify ") +
                  "FNF Settlement"
                ) : (
                  <FaTasks
                    className={`${
                      eligibleForSettlement && verificationPending
                        ? "text-primary"
                        : "text-muted"
                    }`}
                    size={18}
                    title={
                      (record?.fnf_approval_status === 2
                        ? "View "
                        : "Verify ") + "FNF Settlement"
                    }
                  />
                )}
              </button>
            )}

            {/* <button
              disabled={record?.fnf_approval_status === 2 ? false : true}
              className={`${
                isModalVisible ? `btn btn-outline-success` : "dropdown-item"
              }`}
            >
              {isModalVisible ? (
                "Export excel"
              ) : (
                <PiMicrosoftExcelLogoFill
                  className={`${
                    record?.fnf_approval_status === 2
                      ? "text-primary"
                      : "text-muted"
                  }`}
                  title="Export fnf excel"
                  size={18}
                />
              )}
            </button> */}
          </div>
        );
      },
    },
  ];
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={
            action
              ? `${
                  action === "verifyFNFSettlement" ? "Verify " : ""
                }Full and Final Settlement Of ${selectedEmployee.employee_name}`
              : "Employees FNF"
          }
          title={action ? "Employees FNF" : "Dashboard"}
          subtitle={action ? "Full and Final Settlement" : "Employees FNF"}
          titleLink={action ? "employees-resignation" : null}
          onBackButton={() => {
            action ? navigate("../employee-fnf") : navigate(-1);
          }}
        />
        {!fullAndFinalSettlement ? (
          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  scroll={{ x: 1000 }}
                  className="table-striped"
                  columns={columns}
                  dataSource={employeeResignations?.reverse()}
                  rowKey={(record) => record.id}
                  loading={loading.show}
                />
              </div>
            </div>
          </div>
        ) : (
          <FullAndFinalSettlement
            {...{
              selectedEmployee: selectedEmployee.employee_id,
              setFullAndFinalSettlement,
              action,
              fetchEmployeesResignations,
              fnfApprover,
            }}
          />
        )}
        {selectedRow && (
          <RowDetailModal
            visible={isModalVisible}
            onClose={closeModal}
            record={selectedRow}
            columns={columns}
            title="Separation details"
          />
        )}
      </div>
      <Loader show={loading.show} loadingMessage={loading.message} />
    </div>
  );
};

export default EmployeeFnf;
